import { useEffect, useRef, useState } from 'react'

export interface ZipLocation {
  zip: string
  city: string
  state: string
}

type LookupZip = (zip: string) => Promise<{ data: ZipLocation }>

export function normalizeZipInput(value: string): string {
  return value.replace(/[^\d-]/g, '').slice(0, 10)
}

export function canonicalZip(value: string): string | null {
  if (/^\d{5}$/.test(value)) return value
  if (/^\d{5}-\d{4}$/.test(value)) return value.slice(0, 5)
  return null
}

interface UseZipLocationOptions {
  zip: string
  onCity: (city: string) => void
  onState: (state: string) => void
  lookupZip: LookupZip
  enabled?: boolean
}

export function useZipLocation({ zip, onCity, onState, lookupZip, enabled = true }: UseZipLocationOptions) {
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const requestSequence = useRef(0)
  const lastLookup = useRef<string>('')

  useEffect(() => {
    if (!enabled) return
    const normalizedZip = canonicalZip(zip)
    if (!normalizedZip) {
      lastLookup.current = ''
      setMessage(null)
      return
    }
    if (lastLookup.current === normalizedZip) return

    lastLookup.current = normalizedZip
    const sequence = ++requestSequence.current
    setLoading(true)
    setMessage(null)

    void lookupZip(normalizedZip)
      .then((response) => {
        if (sequence !== requestSequence.current || canonicalZip(zip) !== normalizedZip) return
        onCity(response.data.city)
        onState(response.data.state.toUpperCase())
      })
      .catch((error: any) => {
        if (sequence !== requestSequence.current || canonicalZip(zip) !== normalizedZip) return
        setMessage(error?.response?.status === 404
          ? `ZIP ${normalizedZip} not found. You can still enter city/state manually.`
          : 'Could not auto-fill ZIP. You can still enter city/state manually.')
      })
      .finally(() => {
        if (sequence === requestSequence.current) setLoading(false)
      })
  }, [enabled, lookupZip, onCity, onState, zip])

  return { loading, message }
}
